import {
  CONNECTION_HEALTH,
  PR_KNOWLEDGE_STATUSES,
  WEBHOOK_HEALTH,
} from '../../network/httpClients/RepositoryClient/consts';
import type {
  PrKnowledgeStatus,
  RepositorySortField,
} from '../../network/httpClients/RepositoryClient/api.schema';

export const REPOSITORIES_TAB = {
  REPOSITORIES: 'repositories',
  INSTALLATIONS: 'installations',
  RELATIONSHIPS: 'relationships',
};

export const REPOSITORY_STATUSES = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  DISABLED: 'disabled',
  ERROR: 'error',
  FAILED: 'failed',
  PENDING: 'pending',
  SUSPENDED: 'suspended',
  SYNCING: 'syncing',
};

export const COLUMN_INSTALLATIONS_IDS = {
  ID: 'id',
  NAME: 'name',
  DESCRIPTION: 'description',
  PROVIDER: 'provider',
  REPOSITORIES_COUNT: 'repositoriesCount',
  LAST_CONNECTION_TESTED_AT: 'lastConnectionTestedAt',
  CONNECTION_STATUS: 'connectionStatus',
  AVATAR_URL: 'avatarUrl',
  ACTIONS: 'actions',
} as const;

export const COLUMN_REPOSITORIES_IDS = {
  ID: 'id',
  REPOSITORY: 'repository',
  INSTALLATION: 'installation',
  INSTALLATION_ID: 'installationId',
  PROVIDER: 'provider',
  ACTIONS: 'actions',
  IS_CODE_REVIEW: 'isCodeReview',
  IS_CODE_BASE_CONTEXT: 'isCodeBaseContext',
  FULL_NAME: 'fullName',
  NAME: 'name',
  TENANT: 'tenant',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  ACTIVE: 'active',
  REPOSITORY_STATUS: 'status',
  CAPABILITIES: 'capabilities',
  LAST_SYNCED_AT: 'lastSyncedAt',
  ACTIVE_RELATIONSHIP_COUNT: 'activeRelationshipCount',
  CONNECTION_HEALTH: 'connectionHealth',
  PR_KNOWLEDGE: 'prKnowledge',
} as const;

/**
 * Maps sortable repository-table column ids to the backend sort field. Only
 * columns the backend can actually sort appear here — every other column is
 * marked `enableSorting: false` so it renders as plain (non-clickable) text.
 */
export const COLUMN_ID_TO_SORT_FIELD: Record<string, RepositorySortField> = {
  [COLUMN_REPOSITORIES_IDS.REPOSITORY]: 'full_name',
  [COLUMN_REPOSITORIES_IDS.PR_KNOWLEDGE]: 'pr_knowledge',
};

export const COLUMN_INSTALLATIONS_WIDTH = {
  [COLUMN_INSTALLATIONS_IDS.NAME]: 220,
  [COLUMN_INSTALLATIONS_IDS.PROVIDER]: 160,
  [COLUMN_INSTALLATIONS_IDS.REPOSITORIES_COUNT]: 170,
  [COLUMN_INSTALLATIONS_IDS.LAST_CONNECTION_TESTED_AT]: 215,
  [COLUMN_INSTALLATIONS_IDS.CONNECTION_STATUS]: 225,
  [COLUMN_INSTALLATIONS_IDS.ACTIONS]: 20,
};

export const COLUMN_REPOSITORIES_WIDTH = {
  [COLUMN_REPOSITORIES_IDS.REPOSITORY]: 300,
  [COLUMN_REPOSITORIES_IDS.INSTALLATION]: 130,
  [COLUMN_REPOSITORIES_IDS.PROVIDER]: 158,
  [COLUMN_REPOSITORIES_IDS.CAPABILITIES]: 180,
  [COLUMN_REPOSITORIES_IDS.IS_CODE_REVIEW]: 120,
  [COLUMN_REPOSITORIES_IDS.REPOSITORY_STATUS]: 200,
  [COLUMN_REPOSITORIES_IDS.ACTIVE_RELATIONSHIP_COUNT]: 190,
  [COLUMN_REPOSITORIES_IDS.CONNECTION_HEALTH]: 185,
  [COLUMN_REPOSITORIES_IDS.PR_KNOWLEDGE]: 200,
  [COLUMN_REPOSITORIES_IDS.ACTIONS]: 40,
};

export const BADGE_STATUS = {
  SUCCESS: 'success',
  WARNING: 'warning',
  DESTRUCTIVE: 'danger',
  OUTLINE: 'neutral',
  EMPHASIS: 'emphasis',
  IN_PROGRESS: 'inProgress',
} as const;

export const REPOSITORY_STATUS_TO_BADGE_STATUS: Record<
  string,
  (typeof BADGE_STATUS)[keyof typeof BADGE_STATUS]
> = {
  [REPOSITORY_STATUSES.CONNECTED]: BADGE_STATUS.SUCCESS,
  [REPOSITORY_STATUSES.ERROR]: BADGE_STATUS.DESTRUCTIVE,
  [REPOSITORY_STATUSES.FAILED]: BADGE_STATUS.DESTRUCTIVE,
  [REPOSITORY_STATUSES.DISCONNECTED]: BADGE_STATUS.OUTLINE,
  [REPOSITORY_STATUSES.DISABLED]: BADGE_STATUS.OUTLINE,
  [REPOSITORY_STATUSES.PENDING]: BADGE_STATUS.OUTLINE,
  [REPOSITORY_STATUSES.SUSPENDED]: BADGE_STATUS.DESTRUCTIVE,
  [REPOSITORY_STATUSES.SYNCING]: BADGE_STATUS.EMPHASIS,
};

export const WEBHOOK_HEALTH_TO_BADGE_STATUS: Record<
  string,
  (typeof BADGE_STATUS)[keyof typeof BADGE_STATUS]
> = {
  [WEBHOOK_HEALTH.HEALTHY]: BADGE_STATUS.SUCCESS,
  [WEBHOOK_HEALTH.TEMPORARILY_DISABLED]: BADGE_STATUS.DESTRUCTIVE,
  [WEBHOOK_HEALTH.DISABLED]: BADGE_STATUS.OUTLINE,
};

export const CONNECTION_HEALTH_TO_BADGE_STATUS: Record<
  string,
  (typeof BADGE_STATUS)[keyof typeof BADGE_STATUS]
> = {
  [CONNECTION_HEALTH.HEALTHY]: BADGE_STATUS.SUCCESS,
  [CONNECTION_HEALTH.DEGRADED]: BADGE_STATUS.WARNING,
  [CONNECTION_HEALTH.DISABLED]: BADGE_STATUS.DESTRUCTIVE,
  [CONNECTION_HEALTH.PENDING]: BADGE_STATUS.OUTLINE,
  [CONNECTION_HEALTH.UNKNOWN]: BADGE_STATUS.OUTLINE,
};

export const PR_KNOWLEDGE_STATUS_TO_BADGE_STATUS: Record<
  string,
  (typeof BADGE_STATUS)[keyof typeof BADGE_STATUS]
> = {
  [PR_KNOWLEDGE_STATUSES.NOT_STARTED]: BADGE_STATUS.OUTLINE,
  // A first index run is blue; once a repo is Active every state stays green,
  // so an update in flight never reads as a regression.
  [PR_KNOWLEDGE_STATUSES.QUEUED]: BADGE_STATUS.IN_PROGRESS,
  [PR_KNOWLEDGE_STATUSES.IN_PROGRESS]: BADGE_STATUS.IN_PROGRESS,
  [PR_KNOWLEDGE_STATUSES.UPDATING]: BADGE_STATUS.SUCCESS,
  [PR_KNOWLEDGE_STATUSES.COMPLETED]: BADGE_STATUS.SUCCESS,
  [PR_KNOWLEDGE_STATUSES.ACTIVE_QUEUED]: BADGE_STATUS.SUCCESS,
  [PR_KNOWLEDGE_STATUSES.ACTIVE_ATTENTION]: BADGE_STATUS.SUCCESS,
  [PR_KNOWLEDGE_STATUSES.FAILED]: BADGE_STATUS.DESTRUCTIVE,
};

export const PR_KNOWLEDGE_REFETCH_INTERVAL = 10000;

/**
 * Statuses where a run is queued or in flight, so a surface showing them keeps
 * polling — a queued run starts on its own and has to transition without a
 * reload. Shared by the side-panel detail poll and the repositories-list poll
 * so both stop on exactly the same terminal set.
 */
export const PR_KNOWLEDGE_TRANSITIONAL_STATUSES: readonly PrKnowledgeStatus[] =
  [
    PR_KNOWLEDGE_STATUSES.QUEUED,
    PR_KNOWLEDGE_STATUSES.ACTIVE_QUEUED,
    PR_KNOWLEDGE_STATUSES.IN_PROGRESS,
    PR_KNOWLEDGE_STATUSES.UPDATING,
  ];

/**
 * How long after an accepted trigger the repositories list is invalidated a
 * second time. The 202 only acknowledges the enqueue, so the marker row the
 * status is derived from can land after the immediate refetch — without this
 * the row would sit on its old status until the next poll tick.
 */
export const PR_KNOWLEDGE_MARKER_SETTLE_DELAY = 2500;

/**
 * Client-side refusal window after a repo's last completed run, mirroring the
 * platform setting `pr_knowledge_status.manual_trigger_cooldown_minutes` — a
 * default this copy can drift from, since a deployment may tune it. Only a
 * pre-click estimate for rendering: a refusal's `Retry-After` is authoritative
 * and outranks it.
 */
export const PR_KNOWLEDGE_COOLDOWN_MINUTES = 30;

/**
 * Manual runs a workspace may have queued or executing at once, mirroring the
 * platform setting `pr_knowledge_status.manual_runs_cap`. Display only — the
 * backend owns the refusal.
 */
export const PR_KNOWLEDGE_MANUAL_RUNS_CAP = 10;

export const PROVIDER_OPTIONS = [
  { value: 'github', label: 'GitHub' },
  { value: 'gitlab', label: 'GitLab' },
  { value: 'bitbucket', label: 'Bitbucket' },
  { value: 'bitbucket_datacenter', label: 'Bitbucket Data Center' },
  { value: 'azure', label: 'Azure' },
  { value: 'gerrit', label: 'Gerrit' },
];

export const PROVIDER_LABEL_MAP: Record<string, string> = Object.fromEntries(
  PROVIDER_OPTIONS.map(({ value, label }) => [value, label]),
);

export const INSTALLATIONS_PAGE_SIZE = 10;
export const INSTALLATIONS_REFETCH_INTERVAL = 60000;
export const INSTALLATIONS_SYNCING_REFETCH_INTERVAL = 5000;
export const CROSS_REPO_EDGES_REFETCH_INTERVAL = 10000;
export const SEARCH_DEBOUNCE_DELAY = 400;
export const REPOSITORIES_PAGE_SIZE = 10;

export const ALL = 'all';
